import { useApiDelete, useApiGet, useApiPost, useApiQuery } from 'src/api';
import { useBuildVars } from 'src/config';
import { useQueryParams } from 'src/views/customerGroups/hooks/hooks';
import { ResponsePreamble } from './useProductGroups';
import { useMutation, useQueryClient } from 'react-query';
import { useAccount } from '@azure/msal-react';
import { getReactQueryOptions } from 'src/views/customerGroups/hooks/customerGroupMutations';

export const ProductSearchFilters = [
  'name',
  'globalPlanId',
  'globalSKU',
] as const;
export type ProductSearchFilter = typeof ProductSearchFilters[number];

export function useProductsQueryParams({ id, pageSize }: any = {}) {
  const { searchQuery, ...rest } = useQueryParams<ProductSearchFilter>({
    filter: {
      field: ProductSearchFilters[0],
      value: '',
    },
    pageSize: pageSize || 25,
  });

  return {
    searchQuery: {
      ...searchQuery,
      ...(id && { id }),
    },
    ...rest,
  };
}

export interface Product {
  globalPlanId: string;
  globalSKU: string;
  id: number;
  lastUpdated: string;
  name: string;
  totalcount: number;
  updatedBy: string;
}
export interface ProductsResponse {
  data: Product[];
  responsePreamble: ResponsePreamble;
}

export function useProductSearchQuery({ id, page, pageSize, ...query }: any) {
  const buildVars = useBuildVars();
  return useApiQuery<ProductsResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductsByProductGroupId',
    {
      ...query,
      page,
      pageSize,
      productGroupId: id,
    },
    {
      enabled:
        Object.values(query as Record<string, string>).reduce(
          (acc, value) => acc + value.length,
          0
        ) > 1 || !!id,
    },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface AddProductList {
  globalServicePlanId: string;
  globalSku: string;
}

export interface AddProductsToProductGroupRequest {
  products: Product[];
}

export function useAddProductsToGroup(id: number) {
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const buildVars = useBuildVars();
  const apiPost = useApiPost({
    host: buildVars.VP_API_BASE,
  });
  return useMutation(
    ({ products }: AddProductsToProductGroupRequest) =>
      apiPost(
        '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/AddProductsToProductGroup',
        {
          modifiedBy: userName,
          product: products.map((product) => ({
            globalServicePlanId: product.globalPlanId,
            globalSku: product.globalSKU,
          })),
          productGroupId: id,
        }
      ),
    getReactQueryOptions(queryClient, [
      '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductGroups',
      '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductsByProductGroupId',
    ])
  );
}

export function useRemoveProductsFromGroup(id: number) {
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const buildVars = useBuildVars();
  const apiDelete = useApiDelete(buildVars.VP_API_BASE);
  return useMutation(
    (productIds: number[]) =>
      apiDelete(
        '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/DeleteProductsfromProductGroup',
        {
          modifiedBy: userName,
          productGroupId: id,
          productGroupMembershipId: productIds,
        }
      ),
    getReactQueryOptions(queryClient, [
      '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductsByProductGroupId',
      '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductGroups',
    ])
  );
}

export interface ServicePlan {
  global_plan_id: string;
  name: string;
  page: number;
  pageSize: number;
}

export interface ServicePlanList {
  data: ServicePlan[];
  responsePreamble: ResponsePreamble;
}

export function useSearchByServicePlan({
  countryCode,
  page,
  pageSize,
  ...query
}: any) {
  const buildVars = useBuildVars();
  return useApiQuery<ServicePlanList>(
    '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/SearchServicePlan',
    {
      ...query,
      page,
      pageSize,
    },
    {
      enabled:
        Object.values(query as Record<string, string>).reduce(
          (acc, value) => acc + value.length,
          0
        ) > 1,
    },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface ProductByGlobalPlanId {
  globalPlanId: string;
  globalSKU: string;
  id: number;
  isAdded: boolean;
  lastUpdated: string;
  productGroupMembershipId: number;
  totalcount: number;
  updatedBy: string;
}

export interface ProductByGlobalPlanIdList {
  data: ProductByGlobalPlanId[];
  responsePreamble: ResponsePreamble;
}

export function useProductListByGlobalPlanId() {
  const buildVars = useBuildVars();
  const apiGet = useApiGet<ProductByGlobalPlanIdList>({
    host: buildVars.VP_API_BASE,
  });
  return useMutation(({ globalPlanId, page, pageSize }: any) =>
    apiGet(
      '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductsByGlobalPlanId',
      {
        globalPlanId,
        page,
        pageSize,
      }
    )
  );
}
